// Shared types/constants for the merged Social Intelligence module
// (replaces SocialStream.tsx + EnhancedSocialStream.tsx's three separate,
// mutually-inconsistent platform constant sets with one).

export interface SocialPost {
  id: string; platform: string; text: string; author: string; author_id: string;
  author_verified: boolean; author_followers: number; timestamp: string; url: string;
  media_urls: string[]; hashtags: string[]; mentions: string[];
  likes: number; shares: number; comments: number; views: number;
  sentiment: 'positive' | 'negative' | 'neutral' | 'unknown';
  sentiment_confidence: number; language: string;
  entities: Array<{ text: string; type: string; confidence: number }>;
  keywords: string[]; topics: string[];
  engagement: { likes: number; shares: number; comments: number; views: number };
  metadata: Record<string, any>;
  // Added by social_relevance_service.py — zero benefit if the frontend never
  // reads them, which was the case in both predecessor pages.
  relevance_score?: number | null;
  matched_terms?: string[] | null;
}

export interface StreamConfig {
  id: string; user_id?: string; keyword: string; platforms: string[];
  is_active: boolean; created_at: string; updated_at: string;
  last_fetch_at?: string; fetch_interval_minutes: number;
  total_posts: number; last_post_count: number;
  email_alerts_enabled?: boolean;
  expires_at?: string;
}

export interface AnalysisResult {
  hashtag_network: { nodes: any[]; edges: any[] };
  top_influencers: Array<{
    author: string; author_id: string; followers: number;
    likes: number; shares: number; comments: number; influence_score?: number; impact_score?: number;
  }>;
  narrative_clusters: Array<{
    terms?: string[]; keywords?: string[]; post_count: number;
    sentiment_breakdown?: { positive?: number; negative?: number; neutral?: number };
    sentiment_distribution?: { positive: number; negative: number; neutral: number };
    dominant_sentiment?: string;
    nws?: number;
    nws_level?: string;
    nws_breakdown?: { velocity: number; negative_concentration: number; platform_spread: number; amplification: number };
  }>;
  timeline: Record<string, number>;
}

export interface NarrativeCorrelation {
  id: string;
  detected_at: string;
  overlap_score: number;
  shared_hashtags: string[];
  status: 'new' | 'acknowledged';
  // Present on /social/streams/{id}/correlations (single-stream view):
  other_stream_id?: string;
  other_keyword?: string;
  // Present on /social/correlations (aggregate, cross-stream view) instead:
  my_stream_id?: string;
  my_keyword?: string;
}

export interface LinkedAccount {
  platform: string; author_id: string; handle: string; display_name: string;
  followers: number; verified: boolean; avatar_url: string | null;
  matched_via: string[];
}

// ─── Platform constants (unified — one source of truth) ──────────────────────

export const PC: Record<string, string> = {
  twitter: '#1da1f2', facebook: '#1877f2', tiktok: '#ff0050',
  youtube: '#ff0000', reddit: '#ff4500', instagram: '#e1306c',
  forums: '#f97316', telegram: '#229ed9', default: '#6b7280',
};

export const PL: Record<string, string> = {
  twitter: 'Twitter', facebook: 'Facebook', tiktok: 'TikTok',
  youtube: 'YouTube', reddit: 'Reddit', instagram: 'Instagram', forums: 'Forums',
  telegram: 'Telegram',
};

export const SC: Record<string, string> = {
  positive: '#22c55e', negative: '#ef4444', neutral: '#6b7280', unknown: '#374151',
};

export const ALL_PLATFORMS = ['twitter', 'facebook', 'tiktok', 'youtube', 'reddit', 'instagram', 'forums'];

// Languages the backend /social/translate endpoint can target — it already
// accepts arbitrary target_lang (not just 'en'); only the picker was missing.
export const TRANSLATE_LANGUAGES: Array<{ value: string; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'ar', label: 'Arabic' },
  { value: 'ru', label: 'Russian' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ur', label: 'Urdu' },
  { value: 'bn', label: 'Bengali' },
  { value: 'ta', label: 'Tamil' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'fa', label: 'Persian' },
  { value: 'tr', label: 'Turkish' },
];

// ─── Relevance tiers ───────────────────────────────────────────────────────────

export function relevanceTier(score: number | null | undefined): { label: string; color: string } | null {
  if (score == null) return null;
  if (score >= 0.66) return { label: 'High', color: '#22c55e' };
  if (score >= 0.33) return { label: 'Medium', color: '#eab308' };
  return { label: 'Low', color: '#6b7280' };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const fmtNum = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

export const isVideoUrl = (url: string) =>
  /\.(mp4|webm|mov|avi|mkv|m3u8)/i.test(url) ||
  ['video', 'tiktok', 'youtube'].some(k => url.toLowerCase().includes(k));

export const proxyUrl = (url: string) => `/api/social/media-proxy?url=${encodeURIComponent(url)}`;

function pick(obj: any, ...keys: string[]): number {
  for (const k of keys) {
    const v = obj?.[k];
    if (v != null && Number(v) > 0) return Number(v);
  }
  return 0;
}

export function parsePosts(data: any): SocialPost[] {
  const raw: any[] = data?.posts ?? data?.graph_data?.nodes ?? [];
  return raw.map((p: any) => {
    const meta = p.metadata ?? {};
    const likes = pick(p, 'likes', 'likeCount', 'like_count', 'favoriteCount', 'favorite_count')
      || pick(meta, 'likes', 'likeCount', 'like_count', 'favoriteCount', 'favorite_count');
    const shares = pick(p, 'shares', 'shareCount', 'share_count', 'retweetCount', 'retweet_count')
      || pick(meta, 'shares', 'shareCount', 'share_count', 'retweetCount', 'retweet_count');
    const comments = pick(p, 'comments', 'commentCount', 'comment_count', 'replyCount', 'reply_count')
      || pick(meta, 'comments', 'commentCount', 'comment_count', 'replyCount', 'reply_count');
    const views = pick(p, 'views', 'viewCount', 'view_count', 'viewsCount', 'views_count', 'impressions')
      || pick(meta, 'views', 'viewCount', 'view_count', 'viewsCount', 'views_count', 'impressions');
    return {
      id: p.id ?? String(Math.random()),
      platform: p.platform ?? 'unknown',
      text: p.text ?? '',
      author: p.author ?? p.label?.replace('@', '') ?? 'Unknown',
      author_id: p.author_id ?? '',
      author_verified: p.author_verified ?? false,
      author_followers: p.author_followers ?? 0,
      timestamp: p.timestamp ?? new Date().toISOString(),
      url: p.url ?? '',
      media_urls: p.media_urls ?? [],
      hashtags: p.hashtags ?? [],
      mentions: p.mentions ?? [],
      likes, shares, comments, views,
      sentiment: p.sentiment ?? 'unknown',
      sentiment_confidence: p.sentiment_confidence ?? 0.5,
      language: p.language ?? 'en',
      entities: (p.entities ?? []).map((e: any) => ({
        text: e.text, type: e.label ?? e.type ?? 'unknown', confidence: e.confidence ?? 0,
      })),
      keywords: p.keywords ?? [],
      topics: p.topics ?? [],
      engagement: p.engagement ?? { likes, shares, comments, views },
      metadata: meta,
      relevance_score: p.relevance_score ?? null,
      matched_terms: p.matched_terms ?? null,
    };
  });
}
